import type { Options } from "focus-trap";
import { tabbable } from "tabbable";
import { FOCUS_TRAP_TIMEOUT_FALLBACK } from "./constants/focusTrap";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { devDebug } from "./loggingHelpers";
import type { FocusTrapProps } from "focus-trap-react";

type TrapContainersType = Parameters<Required<Options>["checkCanFocusTrap"]>[0];

const onActivate = () => {
  devDebug("🪤 Focus trap activated");
};

export const useFocusTrapOptions = <T extends HTMLElement>(
  elRef: MutableRefObject<T | null>
) => {
  const isMountedRef = useRef(true);
  const moRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safeClearTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const safeClearMutationObserver = useCallback(() => {
    if (moRef.current) {
      moRef.current.disconnect();
      moRef.current = null;
    }
  }, []);
  useEffect(() => {
    isMountedRef.current = true; // Reset on each mount
    return () => {
      devDebug("UNMOUNTING");
      safeClearMutationObserver();
      safeClearTimeout();
      elRef.current = null;
      isMountedRef.current = false;
    };
  }, [elRef, safeClearMutationObserver, safeClearTimeout]);
  const checkCanFocusTrap = useCallback(
    (trapContainers: TrapContainersType): Promise<void> =>
      new Promise(resolve => {
        safeClearMutationObserver();
        safeClearTimeout();
        devDebug("CHECK CAN FOCUS TRAP");
        const container = trapContainers[0]; // There is only one container here
        if (!container || tabbable(container).length > 0) {
          devDebug(
            container ? "TABBABLE ELEMENT FOUND IMMEDIATELY" : "NO CONTAINER"
          );
          resolve();
          return;
        }
        // Fallback in case no tabbable element is found.
        timeoutRef.current = setTimeout(() => {
          devDebug("TABBABLE ELEMENT NOT FOUND");
          safeClearMutationObserver();
          if (!isMountedRef.current) {
            return;
          }
          resolve();
        }, FOCUS_TRAP_TIMEOUT_FALLBACK);
        const observerCb: MutationCallback = () => {
          if (!isMountedRef.current) {
            return;
          }
          devDebug("MO CALLBACK");
          if (tabbable(container).length > 0) {
            devDebug("TABBABLE ELEMENT FOUND BY MUTATION OBSERVER");
            safeClearTimeout();
            safeClearMutationObserver();
            resolve();
          }
        };
        moRef.current = new MutationObserver(observerCb);
        moRef.current?.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            "tabindex",
            "disabled",
            "aria-hidden",
            "hidden",
            "open",
          ],
        });
      }),
    [isMountedRef, safeClearMutationObserver, safeClearTimeout]
  );
  // Called after focus is sent to the focus trap
  const onPostActivate = useCallback(() => {
    devDebug("🪤 Focus has been sent to the animated focus trap");
    elRef.current?.classList.add("focus-trap-active");
  }, [elRef]);
  const onDeactivate = useCallback(() => {
    devDebug("🪤 Focus trap being deactivated");
    elRef.current?.classList.remove("focus-trap-active");
  }, [elRef]);
  const focusTrapOptions: FocusTrapProps["focusTrapOptions"] = useMemo(
    () => ({
      checkCanFocusTrap,
      onActivate,
      onDeactivate,
      onPostActivate,
      returnFocusOnDeactivate: true,
    }),
    [checkCanFocusTrap, onDeactivate, onPostActivate]
  );
  return focusTrapOptions;
};
